import { Component, Input, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog } from '@angular/material/dialog';

import { Subscription } from 'rxjs';

import { TerpecaNomination } from 'src/app/models/nomination.model';
import { TerpecaRanking } from 'src/app/models/ranking.model';
import { TerpecaCategory } from 'src/app/models/room.model';
import {
    AccountIdFieldInfo, ApplicationStatus, MIN_ROOMS_FOR_NOMINATOR, MIN_ROOMS_FOR_VOTER, TerpecaUser, UserAuditLogEntryType,
    getIpAddressesFromAuditLog, getStatusString, getTrackerPartners, getUnresolvedNominationYear, getUserAuditLogString,
    isTemporarilyIneligibleToNominate
} from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { PartnerEndpointsService } from 'src/app/services/partner-endpoints.service';
import { SettingsService } from 'src/app/services/settings.service';
import { countryListDisplayName, getLocationString } from 'src/app/utils/misc.utils';
import { DenyDialogComponent } from 'src/app/views/denydialog/denydialog.component';
import { EditApplicantDialogComponent } from 'src/app/views/editapplicantdialog/editapplicantdialog.component';
import { PartnerPlayedDialogComponent, PartnerPlayedDialogData } from 'src/app/views/partnerplayeddialog/partnerplayeddialog.component';
import { RequestResubmitDialogComponent } from 'src/app/views/requestresubmitdialog/requestresubmitdialog.component';
import { environment } from 'src/environments/environment';

import { ReviewApplicantsComponent } from '../reviewapplicants/reviewapplicants.component';
import { SimpleTextInputDialogComponent } from '../simpletextinputdialog/simpletextinputdialog.component';
import { VouchDialogComponent } from '../vouchdialog/vouchdialog.component';

@Component({
  selector: 'app-applicant',
  standalone: false,
  templateUrl: './applicant.component.html',
  styleUrl: './applicant.component.css'
})
export class ApplicantComponent {
  private auth = inject(AuthService);
  settings = inject(SettingsService);
  private db = inject(AngularFirestore);
  private dialog = inject(MatDialog);
  private partnerEndpoints = inject(PartnerEndpointsService);

  @Input() parent: ReviewApplicantsComponent;
  @Input() reviewer: TerpecaUser;
  @Input() user: TerpecaUser;
  nominations: TerpecaNomination[];
  onlineNominations: TerpecaNomination[];
  rankings: TerpecaRanking;
  onlineRankings: TerpecaRanking;
  collisions: Map<string, TerpecaUser>;
  private subscriptions: Subscription[] = [];

  Status = ApplicationStatus;
  minRoomsForVoter = MIN_ROOMS_FOR_VOTER;
  minRoomsForNominator = MIN_ROOMS_FOR_NOMINATOR;
  getStatusString = getStatusString;
  getUserAuditLogString = getUserAuditLogString;
  getLocationString = getLocationString;
  countryListDisplayName = countryListDisplayName;
  isTemporarilyIneligibleToNominate = isTemporarilyIneligibleToNominate;
  getUnresolvedNominationYear = getUnresolvedNominationYear;
  year = environment.currentAwardYear;
  trackerPartners = getTrackerPartners();
  /** Partners with an enabled played-rooms endpoint; only loaded for owners, who are the only ones allowed to ask. */
  private enabledPartnerIds = new Set<string>();

  panelOpened() {
    this.subscriptions.push(this.db.doc<TerpecaUser>(`users/${this.user.uid}`)
      .valueChanges().subscribe((user: TerpecaUser) => {
        this.user = user;
        this.collisions = new Map<string, TerpecaUser>();
        if (this.user.status === ApplicationStatus.PENDING || this.user.upgradeRequested) {
          const tuplesToCheck = [];
          tuplesToCheck.push(['realName', [this.user.realName]]);
          const emails = [this.user.email];
          if (this.user.appEmail) {
            emails.push(this.user.appEmail);
          }
          tuplesToCheck.push(['email', emails]);
          tuplesToCheck.push(['appEmail', emails]);
          if (this.user.facebookUsername) {
            tuplesToCheck.push(['facebookUsername', [this.user.facebookUsername]]);
          }
          if (this.user.instagramUsername) {
            tuplesToCheck.push(['instagramUsername', [this.user.instagramUsername]]);
          }
          if (this.user.discordUsername) {
            tuplesToCheck.push(['discordUsername', [this.user.discordUsername]]);
          }
          if (this.user.linkedinUsername) {
            tuplesToCheck.push(['linkedinUsername', [this.user.linkedinUsername]]);
          }
          if (this.user.mortyAppUsername) {
            tuplesToCheck.push(['mortyAppUsername', [this.user.mortyAppUsername]]);
          }
          if (this.user.escapeTheReviewUserId) {
            tuplesToCheck.push(['escapeTheReviewUserId', [this.user.escapeTheReviewUserId]]);
          }
          if (this.user.escapeAllGrUserId) {
            tuplesToCheck.push(['escapeAllGrUserId', [this.user.escapeAllGrUserId]]);
          }
          if (this.user.escapeGameFrUserId) {
            tuplesToCheck.push(['escapeGameFrUserId', [this.user.escapeGameFrUserId]]);
          }
          if (this.user.escapeRoomersUserId) {
            tuplesToCheck.push(['escapeRoomersUserId', [this.user.escapeRoomersUserId]]);
          }
          if (this.user.escapeTalkNlUserId) {
            tuplesToCheck.push(['escapeTalkNlUserId', [this.user.escapeTalkNlUserId]]);
          }
          if (this.user.lockMeUserId) {
            tuplesToCheck.push(['lockMeUserId', [this.user.lockMeUserId]]);
          }
          if (this.user.theEscapersUserId) {
            tuplesToCheck.push(['theEscapersUserId', [this.user.theEscapersUserId]]);
          }
          const ipsToCheck = [];
          if (this.user.ipAddresses?.length) {
            ipsToCheck.push(...this.user.ipAddresses);
          } else {
            ipsToCheck.push(...getIpAddressesFromAuditLog(this.user));
          }
          if (ipsToCheck.length) {
            const fieldName = 'ipAddresses';
            this.subscriptions.push(this.db.collection<TerpecaUser>('users',
                    ref => ref.where(fieldName, 'array-contains-any', ipsToCheck)
                              .where('status', '>=', ApplicationStatus.PENDING))
              .valueChanges().subscribe((value: TerpecaUser[]) => {
                // We filter out this user's ID to prevent it from showing up as its own duplicate.
                value = value?.filter(u => u.uid !== this.user.uid) || [];
                if (value.length) {
                  this.collisions[fieldName] = value;
                }
              })
            );
            tuplesToCheck.push(['auditLogEntry.ipAddress', ipsToCheck]);
          }
          for (const tuple of tuplesToCheck) {
            this.subscriptions.push(this.db.collection<TerpecaUser>('users',
                    ref => ref.where(tuple[0], 'in', tuple[1])
                              .where('status', '>=', ApplicationStatus.PENDING))
              .valueChanges().subscribe((value: TerpecaUser[]) => {
                // We filter out this user's ID to prevent it from showing up as its own duplicate.
                value = value?.filter(u => u.uid !== this.user.uid) || [];
                if (tuple[0] === 'auditLogEntry.ipAddress') {
                  // We make sure that any IP address hits belong to the applicant and not the reviewer.
                  value = value?.filter(u => u.auditLogEntry.uid === u.uid);
                }
                if (value.length) {
                  this.collisions[tuple[0]] = value;
                }
              })
            );
          }
        }
      })
    );
    if (this.reviewer && this.reviewer.isOwner) {
      this.loadEnabledPartnerIds();
      this.subscriptions.push(this.db.collection<TerpecaNomination>('nominations',
            ref => ref.where('year', '==', this.year)
                      .where('userId', '==', this.user.uid)
                      .where('category', '==', TerpecaCategory.TOP_ROOM)).valueChanges({ idField: 'docId' })
          .subscribe((value: TerpecaNomination[]) => {
            this.nominations = value;
          })
      );
      this.subscriptions.push(this.db.collection<TerpecaNomination>('nominations',
            ref => ref.where('year', '==', this.year)
                      .where('userId', '==', this.user.uid)
                      .where('category', '==', TerpecaCategory.TOP_ONLINE_ROOM)).valueChanges({ idField: 'docId' })
          .subscribe((value: TerpecaNomination[]) => {
            this.onlineNominations = value;
          })
      );
      this.subscriptions.push(this.db.doc<TerpecaRanking>(`rankings/${this.user.uid}-${this.year}-${TerpecaCategory.TOP_ROOM}`)
        .valueChanges().subscribe((rankings: TerpecaRanking) => {
          this.rankings = rankings;
        })
      );
      this.subscriptions.push(this.db.doc<TerpecaRanking>(`rankings/${this.user.uid}-${this.year}-${TerpecaCategory.TOP_ONLINE_ROOM}`)
        .valueChanges().subscribe((rankings: TerpecaRanking) => {
          this.onlineRankings = rankings;
        })
      );
    }
  }

  getSharedIps(u1: TerpecaUser, u2: TerpecaUser) {
    return (u1.ipAddresses || []).filter(value => (u2.ipAddresses || []).includes(value));
  }

  panelClosed() {
    this.subscriptions.forEach(subscription => { subscription.unsubscribe(); });
    this.subscriptions = [];
  }

  pastYears() {
    return this.settings.allYears.filter(key => key < this.year);
  }

  hasPastExperience() {
    for (const y of this.pastYears()) {
      if (this.user.nominationsSubmitted?.includes(y) || this.user.rankingsSubmitted?.includes(y)) {
        return true;
      }
    }
    return false;
  }

  hasVouch() {
    return (this.user.vouchList?.length || 0) > 0;
  }

  hasUpgradeVouch() {
    return (this.user.upgradeVouchList?.length || 0) > 0;
  }

  canApproveVoterOrNominator() {
    // Role changes for already-approved users don't need a new vouch.
    if (this.user.status >= ApplicationStatus.VOTER) {
      return true;
    }
    return this.hasVouch();
  }

  canApproveUpgrade() {
    return this.hasUpgradeVouch();
  }

  async setStatus(status: ApplicationStatus) {
    await this.auth.setApplicationStatus(this.user.uid, status);
    this.parent.refreshData();
  }

  denyApplication() {
    // Find duplicate account email from already-detected collisions
    // Priority: verified email > appEmail > realName > other collision types
    let duplicateEmail = '';
    if (this.collisions) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      const emailCollisions = this.collisions['email'] || [];
      if (emailCollisions.length > 0 && emailCollisions[0].email) {
        duplicateEmail = emailCollisions[0].email;
      } else {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const appEmailCollisions = this.collisions['appEmail'] || [];
        if (appEmailCollisions.length > 0) {
          duplicateEmail = appEmailCollisions[0].email || '';
        } else {
          // eslint-disable-next-line @typescript-eslint/dot-notation
          const realNameCollisions = this.collisions['realName'] || [];
          if (realNameCollisions.length > 0) {
            duplicateEmail = realNameCollisions[0].email || '';
          } else {
            // Check other collision types
            for (const [key, users] of Object.entries(this.collisions)) {
              if (key !== 'email' && key !== 'appEmail' && key !== 'realName' &&
                  key !== 'ipAddresses' && key !== 'auditLogEntry.ipAddress' &&
                  Array.isArray(users) && users.length > 0) {
                duplicateEmail = users[0].email || '';
                if (duplicateEmail) {
                  break;
                }
              }
            }
          }
        }
      }
    }
    const data: any = {
      applicant: this,
      duplicateEmail: duplicateEmail
    };
    this.dialog.open(DenyDialogComponent, { data });
  }

  requestResubmit() {
    const data: any = {
      applicant: this
    };
    this.dialog.open(RequestResubmitDialogComponent, { data, width: '720px', maxWidth: '95vw' });
  }

  editApplicant() {
    const data: any = {
      user: this.user
    };
    this.dialog.open(EditApplicantDialogComponent, { data });
  }

  resubmitRequested() {
    this.parent.refreshData();
  }

  denyRequested() {
    this.parent.refreshData();
  }

  showAddReviewNoteDialog() {
    const data: any = {
      title: 'Add Review Note',
      instructions: 'Add a private note about this applicant regarding their current status in the review process. This note will only be visible to other reviewers.',
      callback: async (note: string) => { await this.addReviewNote(note); }
    };
    this.dialog.open(SimpleTextInputDialogComponent, { data });
  }

  showAddVouchDialog() {
    const data: any = {
      isUpgrade: false,
      callback: async (note: string) => { await this.addVouch(note); }
    };
    this.dialog.open(VouchDialogComponent, { data });
  }

  showAddUpgradeVouchDialog() {
    const data: any = {
      isUpgrade: true,
      callback: async (note: string) => { await this.addUpgradeVouch(note); }
    };
    this.dialog.open(VouchDialogComponent, { data });
  }

  showRemoveVouchDialog() {
    const data: any = {
      title: 'Remove Vouch',
      instructions: 'Please indicate why you are removing your vouch.',
      callback: async (note: string) => { await this.removeVouch(note); }
    };
    this.dialog.open(SimpleTextInputDialogComponent, { data });
  }

  showRemoveUpgradeVouchDialog() {
    const data: any = {
      title: 'Remove Upgrade Vouch',
      instructions: 'Please indicate why you are removing your upgrade vouch.',
      callback: async (note: string) => { await this.removeUpgradeVouch(note); }
    };
    this.dialog.open(SimpleTextInputDialogComponent, { data });
  }

  showRevokeNominationsStatusDialog() {
    const data: any = {
      title: 'Revoke Nominations',
      instructions: 'Please indicate why you are revoking nominations for this user.',
      callback: async (note: string) => {
        await this.addReviewNote(note);
        await this.reopenNominations();
      }
    };
    this.dialog.open(SimpleTextInputDialogComponent, { data });
  }

  showRevokeRankingsStatusDialog() {
    const data: any = {
      title: 'Revoke Rankings',
      instructions: 'Please indicate why you are revoking rankings for this user.',
      callback: async (note: string) => {
        await this.addReviewNote(note);
        await this.reopenRankings();
      }
    };
    this.dialog.open(SimpleTextInputDialogComponent, { data });
  }

  showUpdateShadowbanStatusDialog(on: boolean) {
    const data: any = {
      title: on ? 'Add Shadow Ban' : 'Remove Shadow Ban',
      instructions: `Please indicate why you are ${ on ? 'adding' : 'removing'} a shadow ban for this user.`,
      callback: async (note: string) => { await this.updateShadowbanStatus(on, note); }
    };
    this.dialog.open(SimpleTextInputDialogComponent, { data });
  }

  canWaiveNominationPenalty() {
    return Boolean(this.reviewer?.isOwner && this.user?.status >= ApplicationStatus.NOMINATOR);
  }

  isNominationPenaltyWaived() {
    return Boolean(this.user.nominationPenaltyWaived?.includes(this.year));
  }

  showUpdateNominationPenaltyWaiverDialog(on: boolean) {
    if (!this.canWaiveNominationPenalty()) {
      return;
    }
    const data: any = {
      title: on ? 'Waive Nomination Suspension' : 'Remove Nomination Suspension Waiver',
      instructions: on ?
        `This user nominated in ${this.getUnresolvedNominationYear(this.user, this.year)} without voting. ` +
        `Please indicate why you are allowing them to nominate in ${this.year} anyway.` :
        `Please indicate why you are removing this user's ${this.year} nomination suspension waiver.`,
      callback: async (note: string) => {
        await this.auth.setNominationPenaltyWaived(this.user.uid, on, note);
        this.parent.refreshData();
      }
    };
    this.dialog.open(SimpleTextInputDialogComponent, { data });
  }

  showUpdateDisabledStatusDialog(on: boolean) {
    if (!this.canDisableAccount()) {
      return;
    }
    const data: any = {
      title: on ? 'Disable Account' : 'Enable Account',
      instructions: `Please indicate why you are ${on ? 'disabling' : 'enabling'} this account.`,
      callback: async (note: string) => { await this.updateDisabledStatus(on, note); }
    };
    this.dialog.open(SimpleTextInputDialogComponent, { data });
  }

  async addReviewNote(note: string) {
    await this.auth.addReviewNote(this.user.uid, note);
  }

  hasPendingReviewNote() {
    return this.user.auditLogEntry?.entryType === UserAuditLogEntryType.REVIEW_NOTE_ADDED;
  }

  async addVouch(note: string) {
    await this.auth.addVouch(this.user.uid, note);
  }

  async removeVouch(note: string) {
    await this.auth.removeVouch(this.user.uid, note);
  }

  async addUpgradeVouch(note: string) {
    await this.auth.addUpgradeVouch(this.user.uid, note);
  }

  async removeUpgradeVouch(note: string) {
    await this.auth.removeUpgradeVouch(this.user.uid, note);
  }

  async updateShadowbanStatus(on: boolean, note: string) {
    await this.auth.setShadowBan(this.user.uid, on, note);
  }

  canDisableAccount() {
    return Boolean(this.reviewer?.isOwner && this.user && !this.user.isOwner && this.user.uid !== this.reviewer.uid);
  }

  private async loadEnabledPartnerIds() {
    try {
      this.enabledPartnerIds = await this.partnerEndpoints.loadEnabledPartnerIds();
    } catch (error) {
      console.error('could not list partner endpoints', error);
    }
  }

  canFetchPlayedRooms(partner: AccountIdFieldInfo) {
    return Boolean(this.reviewer?.isOwner && partner.partnerId && this.enabledPartnerIds.has(partner.partnerId));
  }

  showPlayedRooms(partner: AccountIdFieldInfo) {
    const data: PartnerPlayedDialogData = { user: this.user, partner };
    this.dialog.open(PartnerPlayedDialogComponent, { data, width: '1000px', maxWidth: '95vw' });
  }

  async updateDisabledStatus(on: boolean, note: string) {
    if (!this.canDisableAccount()) {
      return;
    }
    await this.auth.setDisabled(this.user.uid, on, note);
  }

  canChangeNominationStatus() {
    return this.auth.canChangeNominationStatus(this.user);
  }

  async submitNominations() {
    if (!this.canChangeNominationStatus()) {
      return;
    }
    this.auth.submitNominations(this.user);
  }

  async reopenNominations() {
    if (!this.canChangeNominationStatus()) {
      return;
    }
    this.auth.reopenNominations(this.user);
  }

  canChangeRankingStatus() {
    return this.auth.canChangeRankingStatus(this.user);
  }

  async submitRankings() {
    if (!this.canChangeRankingStatus()) {
      return;
    }
    this.auth.submitRankings(this.user);
  }

  async reopenRankings() {
    if (!this.canChangeRankingStatus()) {
      return;
    }
    this.auth.reopenRankings(this.user);
  }

  getTimeInQueue(): string {
    // Check for pending applicants or upgrade requests
    const isPendingOrUpgrade = (this.user.status === ApplicationStatus.PENDING) ||
                                (this.user.status > ApplicationStatus.PENDING && this.user.upgradeRequested);
    if (!isPendingOrUpgrade || !this.user.auditLogEntry?.timestamp) {
      return '';
    }
    const timestamp = this.user.auditLogEntry.timestamp;
    const now = new Date();
    const queueStart = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    const diffMs = now.getTime() - queueStart.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Only show age if 1 day or more
    if (diffDays >= 1) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
    }
    return '';
  }

  isNew(): boolean {
    // Check for pending applicants or upgrade requests
    const isPendingOrUpgrade = (this.user.status === ApplicationStatus.PENDING) ||
                                (this.user.status > ApplicationStatus.PENDING && this.user.upgradeRequested);
    if (!isPendingOrUpgrade || !this.user.auditLogEntry?.timestamp) {
      return false;
    }
    // Check if the last update was from the applicant themselves
    return this.user.auditLogEntry.uid === this.user.uid;
  }
}
